import { Navigate, Outlet, createBrowserRouter, useLocation } from 'react-router';
import { Spinner } from '@/components/ui';
import {
  ConfirmEmailChangePage,
  ForgotPasswordPage,
  LoginPage,
  ResetPasswordPage,
  SignupPage,
  VerifyEmailPage,
} from '@/features/auth/AuthPages';
import { useSession } from '@/features/auth/session';
import { BusinessProvider, useBusiness } from '@/features/business/BusinessProvider';
import {
  ClaimVerifyPage,
  OnboardingPage,
  TeamAcceptPage,
} from '@/features/business/OnboardingPages';
import { DashboardLayout } from './DashboardLayout';

// Feature pages load on demand so the login screen and shell stay small.
function page<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M & string) {
  return async () => ({ Component: (await load())[name] as React.ComponentType });
}

function RequireAuth() {
  const { data, isPending } = useSession();
  const location = useLocation();
  if (isPending) return <Spinner />;
  if (!data)
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  return (
    <BusinessProvider>
      <Outlet />
    </BusinessProvider>
  );
}

function RequireBusiness() {
  const { businesses, loading } = useBusiness();
  if (loading) return <Spinner />;
  if (!businesses.length) return <Navigate to="/onboarding" replace />;
  return <DashboardLayout />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/signup', element: <SignupPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/confirm-email-change', element: <ConfirmEmailChangePage /> },
  {
    element: <RequireAuth />,
    children: [
      { path: '/onboarding', element: <OnboardingPage /> },
      { path: '/claim/verify', element: <ClaimVerifyPage /> },
      { path: '/team/accept', element: <TeamAcceptPage /> },
      {
        element: <RequireBusiness />,
        children: [
          {
            index: true,
            lazy: page(() => import('@/features/analytics/AnalyticsPages'), 'OverviewPage'),
          },
          {
            path: 'reviews',
            lazy: page(() => import('@/features/reviews/ReviewsPage'), 'ReviewsPage'),
          },
          {
            path: 'invitations',
            lazy: page(() => import('@/features/invitations/InvitationsPage'), 'InvitationsPage'),
          },
          {
            path: 'analytics',
            lazy: page(() => import('@/features/analytics/AnalyticsPages'), 'AnalyticsPage'),
          },
          {
            path: 'customers',
            lazy: page(() => import('@/features/catalog/CatalogPages'), 'CustomersPage'),
          },
          {
            path: 'products',
            lazy: page(() => import('@/features/catalog/products/ProductsPage'), 'ProductsPage'),
          },
          {
            path: 'locations',
            lazy: page(() => import('@/features/catalog/CatalogPages'), 'LocationsPage'),
          },
          {
            path: 'widgets',
            lazy: page(() => import('@/features/widgets/WidgetsPage'), 'WidgetsPage'),
          },
          {
            path: 'widgets/:id',
            lazy: page(() => import('@/features/widgets/WidgetEditorPage'), 'WidgetEditorPage'),
          },
          {
            path: 'integrations',
            lazy: page(
              () => import('@/features/integrations/IntegrationsPage'),
              'IntegrationsPage',
            ),
          },
          { path: 'team', lazy: page(() => import('@/features/team/TeamPage'), 'TeamPage') },
          {
            path: 'billing',
            lazy: page(() => import('@/features/billing/BillingPage'), 'BillingPage'),
          },
          {
            path: 'settings',
            lazy: page(() => import('@/features/settings/SettingsPage'), 'SettingsPage'),
          },
          {
            path: 'account',
            lazy: page(() => import('@/features/account/AccountPage'), 'AccountPage'),
          },
          {
            path: 'notifications',
            lazy: page(() => import('@/features/settings/NotificationsPage'), 'NotificationsPage'),
          },
          { path: 'help', lazy: page(() => import('@/features/help/HelpPage'), 'HelpPage') },
          {
            path: 'admin/support',
            lazy: page(() => import('@/features/admin/SupportInbox'), 'SupportInboxPage'),
          },
          {
            path: 'admin/moderation',
            lazy: page(() => import('@/features/admin/ModerationPage'), 'ModerationPage'),
          },
          {
            path: 'admin/activity',
            lazy: page(() => import('@/features/admin/ActivityPage'), 'ActivityPage'),
          },
          {
            path: 'admin/staff',
            lazy: page(() => import('@/features/admin/StaffPage'), 'StaffPage'),
          },
          {
            path: 'admin/enforcement',
            lazy: page(() => import('@/features/enforcement/TrustSafetyPage'), 'TrustSafetyPage'),
          },
          {
            path: 'admin/enforcement/:id',
            lazy: page(
              () => import('@/features/enforcement/BusinessEnforcementPage'),
              'BusinessEnforcementPage',
            ),
          },
          {
            path: 'admin/plans',
            lazy: page(() => import('@/features/plans/PlansPage'), 'PlansPage'),
          },
          {
            path: 'admin/free-plans',
            lazy: page(() => import('@/features/plan-grants/FreePlansPage'), 'FreePlansPage'),
          },
          {
            path: 'admin/free-plans/:id',
            lazy: page(
              () => import('@/features/plan-grants/BusinessFreePlanPage'),
              'BusinessFreePlanPage',
            ),
          },
          {
            path: 'connect/wordpress',
            lazy: page(
              () => import('@/features/integrations/ConnectWordPressPage'),
              'ConnectWordPressPage',
            ),
          },
          {
            path: 'admin/analytics',
            lazy: page(
              () => import('@/features/platform-analytics/AnalyticsPage'),
              'AnalyticsPage',
            ),
          },
          {
            path: 'admin/businesses',
            lazy: page(
              () => import('@/features/platform-analytics/BusinessesPage'),
              'BusinessesPage',
            ),
          },
          {
            path: 'admin/businesses/:id',
            lazy: page(
              () => import('@/features/platform-analytics/BusinessDetailPage'),
              'BusinessDetailPage',
            ),
          },
          {
            path: 'admin/organisations/:id',
            lazy: page(
              () => import('@/features/platform-analytics/OrganisationDetailPage'),
              'OrganisationDetailPage',
            ),
          },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
